import React, { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Loader2, Sparkles, Upload, Instagram, Download, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ImageStudio = () => {
  const [engines, setEngines] = useState([]);
  const [engine, setEngine] = useState("gpt-image-2");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [sending, setSending] = useState(false);
  const [driveConnected, setDriveConnected] = useState(false);
  const [refFiles, setRefFiles] = useState([]);
  const fileRef = useRef(null);
  const refInputRef = useRef(null);

  const supportsEdit = engines.find((e) => e.id === engine)?.supports_edit;
  const editMode = supportsEdit && refFiles.length > 0;

  useEffect(() => {
    api.get("/images/engines").then((r) => setEngines(r.data || []));
    api.get("/images/list").then((r) => setItems(r.data || []));
    api
      .get("/integrations/google/status")
      .then((r) => setDriveConnected(!!r.data?.connected))
      .catch(() => {});
  }, []);

  // Open a preview and prime its caption (no effect → no set-state-in-effect).
  const openPreview = (img) => {
    setPreview(img);
    setCaption(img?.prompt || "");
  };

  const downloadImage = () => {
    if (!preview?.data_url) return;
    const a = document.createElement("a");
    a.href = preview.data_url;
    a.download = `clydeflow-${preview.image_id}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const sendToInstagram = async () => {
    if (!preview) return;
    setSending(true);
    try {
      // Copy caption to clipboard so it's ready to paste on the phone.
      try {
        await navigator.clipboard.writeText(caption || "");
      } catch {
        /* clipboard may be blocked; non-fatal */
      }

      if (driveConnected) {
        const { data } = await api.post("/integrations/google/push-image", {
          image_id: preview.image_id,
        });
        toast.success("Saved to Google Drive + caption copied.", {
          description: "Open Instagram on your phone, grab it from Drive, paste the caption.",
          action: data?.webViewLink
            ? { label: "Open Drive", onClick: () => window.open(data.webViewLink, "_blank") }
            : undefined,
        });
      } else {
        downloadImage();
        toast.success("Image downloaded + caption copied.", {
          description: "Connect Google Drive to send straight to your phone next time.",
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send to Instagram.");
    } finally {
      setSending(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/images/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((cur) => [data, ...cur]);
      openPreview(data);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onRefSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setRefFiles((cur) => [...cur, ...picked].slice(0, 16));
    if (refInputRef.current) refInputRef.current.value = "";
  };

  const removeRef = (idx) => setRefFiles((cur) => cur.filter((_, i) => i !== idx));

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Give it a prompt first.");
    setBusy(true);
    try {
      let data;
      if (editMode) {
        const fd = new FormData();
        fd.append("prompt", prompt);
        fd.append("aspect", "1:1");
        refFiles.forEach((f) => fd.append("files", f));
        ({ data } = await api.post("/images/edit", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180000,
        }));
        setRefFiles([]);
      } else {
        ({ data } = await api.post("/images/generate", { prompt, engine }, { timeout: 180000 }));
      }
      setItems((cur) => [data, ...cur]);
      openPreview(data);
      toast.success(editMode ? "Edited image ready." : "Image ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image gen failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="image-studio-panel">
      <div className="bg-[#0A192F] border border-white/10 rounded-sm p-6">
        <div className="flex items-center gap-2 text-cyan-300 font-mono text-[10px] uppercase tracking-[0.3em] mb-3">
          <Sparkles size={14} /> Image Studio
        </div>
        <h3 className="font-heading text-2xl font-bold text-white">Generate.</h3>
        <p className="mt-1 text-slate-400 text-sm">
          Pick an engine. Type the prompt. Ship the image.
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          {engines.map((e) => (
            <button
              key={e.id}
              onClick={() => setEngine(e.id)}
              data-testid={`image-engine-${e.id}`}
              className={[
                "px-4 py-2 text-xs font-mono uppercase tracking-widest rounded-sm border transition-all",
                engine === e.id
                  ? "bg-cyan-400/10 text-cyan-300 border-cyan-400/40"
                  : "bg-transparent text-slate-400 border-white/10 hover:border-white/30",
              ].join(" ")}
            >
              {e.label}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <input
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Glasgow skyline at dusk, neon cyan, cinematic"
            data-testid="image-prompt-input"
            className="flex-1 px-4 py-3 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white placeholder-slate-500 text-sm rounded-sm"
          />
          <button
            onClick={generate}
            disabled={busy}
            data-testid="image-generate-btn"
            className="px-5 py-3 bg-[#00F0FF] text-[#050B14] font-bold text-sm rounded-sm hover:bg-[#33F3FF] disabled:opacity-60 flex items-center gap-2"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}
            {editMode ? "Edit" : "Generate"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onUpload}
            data-testid="image-upload-input"
            className="hidden"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="image-upload-btn"
            className="px-5 py-3 bg-transparent border border-white/20 hover:border-cyan-400/50 text-white font-bold text-sm rounded-sm disabled:opacity-60 flex items-center gap-2"
          >
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            Upload
          </button>
        </div>

        {supportsEdit && (
          <div className="mt-4" data-testid="image-ref-section">
            <input
              ref={refInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={onRefSelect}
              data-testid="image-ref-input"
              className="hidden"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => refInputRef.current?.click()}
                data-testid="image-add-ref-btn"
                className="px-4 py-2 text-xs font-mono uppercase tracking-widest rounded-sm border border-dashed border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 flex items-center gap-2"
              >
                <ImagePlus size={14} /> Add reference image
              </button>
              {refFiles.map((f, i) => (
                <div
                  key={i}
                  data-testid={`image-ref-thumb-${i}`}
                  className="relative w-14 h-14 rounded-sm overflow-hidden border border-white/15"
                >
                  <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeRef(i)}
                    data-testid={`image-ref-remove-${i}`}
                    className="absolute top-0 right-0 bg-black/70 text-white p-0.5 hover:bg-red-500"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-slate-500">
              {refFiles.length > 0
                ? `Edit mode: your prompt will transform the ${refFiles.length} reference image${refFiles.length > 1 ? "s" : ""} (gpt-image-2).`
                : "Optional — add a reference image to edit/remix it instead of generating from scratch."}
            </p>
          </div>
        )}
        {busy && (
          <p className="mt-3 text-[11px] font-mono text-cyan-300/80" data-testid="image-busy-hint">
            {editMode ? "Editing" : "Generating"} with {engine}… this can take up to 90s.
          </p>
        )}
      </div>

      {preview && (
        <div className="bg-[#0A192F] border border-white/10 rounded-sm p-4" data-testid="image-preview">
          <img src={preview.data_url} alt={preview.prompt} className="w-full max-h-[60vh] object-contain" />

          <label className="mt-4 block text-[10px] font-mono uppercase tracking-[0.3em] text-slate-500 mb-1">
            Instagram caption
          </label>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={3}
            data-testid="image-caption-input"
            className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white placeholder-slate-500 text-sm rounded-sm resize-none"
            placeholder="Write your caption…"
          />

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={sendToInstagram}
              disabled={sending}
              data-testid="image-send-instagram-btn"
              className="px-5 py-2.5 font-bold text-xs uppercase tracking-widest rounded-sm flex items-center gap-2 bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white hover:opacity-90 disabled:opacity-60"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Instagram size={14} />}
              Send to Instagram
            </button>
            <button
              onClick={downloadImage}
              data-testid="image-download-btn"
              className="px-4 py-2.5 font-bold text-xs uppercase tracking-widest rounded-sm flex items-center gap-2 bg-transparent border border-white/20 hover:border-cyan-400/50 text-white"
            >
              <Download size={14} /> Download
            </button>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">
            {driveConnected
              ? "Saves to Google Drive + copies your caption — post from your phone in seconds."
              : "Downloads the image + copies your caption. Connect Google Drive to send straight to your phone."}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="image-gallery">
        {items.map((it) => (
          <button
            key={it.image_id}
            onClick={() => openPreview(it)}
            data-testid={`image-thumb-${it.image_id}`}
            className="group relative bg-[#0A192F] border border-white/10 hover:border-cyan-400/40 rounded-sm overflow-hidden aspect-square"
          >
            {it.data_url && (
              <img src={it.data_url} alt={it.prompt} className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/90 to-transparent text-[10px] text-slate-300 line-clamp-2 text-left opacity-0 group-hover:opacity-100 transition-opacity">
              {it.prompt}
            </div>
          </button>
        ))}
        {items.length === 0 && (
          <p className="col-span-full text-slate-500 text-sm">No images yet.</p>
        )}
      </div>
    </div>
  );
};

export default ImageStudio;
